/* eslint-disable react-perf/jsx-no-new-array-as-prop */
import { Button, Card, Divider, Flex, Group, Loader, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useGetResultEmpresa } from "@/modules/media-grupo/hooks/useGetResultEmpresa";
import useGetProjetoById from "@/modules/projetos/hooks/useGetProjetoById";
import useUserStore from "@/modules/users/store";
import useGetQuestionarioItem from "@/src/routes/responder-questionario-externo-page/hooks/useGetQuestionarioFromUrl";
import useGetProjetoFromUrl from "./useGetProjetoFromUrl";
import useGetQuestionarioFromUrl from "../estrategia-list/useGetQuestionarioFromUrl";
import RadarNota from "../radar/RadarNota";

const RESULTADO_DA_EMPRESA = "Nota Oficial";
type Properties = {
  grupos: TGruposDiagnostico[];
};

type WrapperProperties<T> = {
  children: React.ReactNode;
  items: T[];
};

/**
 *  Wrapper que condiciona a renderização do children baseado na quantidade de items
 * @param root0 Props
 * @param root0.children Componentes a serem renderizados
 * @param root0.items Itens a serem verificados
 * @returns Componente a ser renderizado
 */
function ConditionalWrapper<T>({ children, items }: WrapperProperties<T>) {
  if (!items || items.length === 0) {
    return null;
  }

  return children;
}

const CardResultados = ({ grupos }: Properties) => {
  const [opened, { toggle }] = useDisclosure(true);
  const licenciadaId = useUserStore((state) => state.activeLicenciadaId);

  const { idProjeto, idEmpresa, id, idEmpresaProjeto } = useParams<{
    idProjeto: string;
    idEmpresa: string;
    id: string;
    idEmpresaProjeto: string;
  }>();
  const projetoByIdResult = useGetProjetoById(idEmpresaProjeto ?? "", idProjeto ?? "", licenciadaId);
  const questionarioByIdResult = useGetQuestionarioItem(idEmpresaProjeto ?? "", id ?? "");

  const projetoFromUrlResult = useGetProjetoFromUrl();
  const questionarioFromUrlResult = useGetQuestionarioFromUrl();

  const projeto = idEmpresaProjeto ? projetoByIdResult.data : projetoFromUrlResult.data;

  const questionario = idEmpresaProjeto ? questionarioByIdResult.data : questionarioFromUrlResult.data;

  const { load, loading, resultEmpresa, resultCategoria, resultGeral, resultRegiao, resultPrimeiroLugar } =
    useGetResultEmpresa(idEmpresaProjeto);
  const resultAutoAvaliacao = useMemo(
    () => (resultEmpresa ? resultEmpresa.map((row) => ({ segment: row.segment, value: row.autoAvaliacao || 0 })) : []),
    [resultEmpresa]
  );
  const resultEmpresaDataset = useMemo(
    () => [buildDataset("Nota Oficial", resultEmpresa || []), buildDataset("Autoavaliação", resultAutoAvaliacao)],
    [resultEmpresa, resultAutoAvaliacao]
  );

  const resultCategoriaDataset = useMemo(
    () => [
      buildDataset(RESULTADO_DA_EMPRESA, resultEmpresa || []),
      buildDataset("Média do Segmento", resultCategoria || [])
    ],
    [resultEmpresa, resultCategoria]
  );

  const resultRegiaoDataset = useMemo(
    () => [buildDataset(RESULTADO_DA_EMPRESA, resultEmpresa || []), buildDataset("Média Regional", resultRegiao || [])],
    [resultEmpresa, resultRegiao]
  );

  const resultGeralDataset = useMemo(
    () => [buildDataset(RESULTADO_DA_EMPRESA, resultEmpresa || []), buildDataset("Média global", resultGeral || [])],
    [resultEmpresa, resultGeral]
  );

  const resultPrimeiroLugarDataset = useMemo(
    () => [
      buildDataset(RESULTADO_DA_EMPRESA, resultEmpresa || []),
      buildDataset("1º Lugar", resultPrimeiroLugar || [])
    ],
    [resultEmpresa, resultPrimeiroLugar]
  );

  const labels = useMemo(() => grupos.map((grupo) => grupo.nome), [grupos]);

  useEffect(() => {
    if (questionario && projeto && idEmpresa && grupos.length > 0) {
      void load(questionario.id, projeto.id, idEmpresa, grupos);
    }
  }, [questionario?.id, projeto?.id, idEmpresa, load]);
  if (loading) {
    return (
      <Card withBorder shadow="md" style={{ flex: 1, marginBottom: 15 }}>
        <Group justify="center">
          <Loader size="md" />
        </Group>
      </Card>
    );
  }

  return (
    <Card withBorder shadow="md" style={{ flex: 1, marginBottom: 15 }}>
      <Flex justify="space-between" align="center">
        <Text size="xl">Resumo dos resultados</Text>
        <Button
          variant="transparent"
          size="compact-sm"
          rightSection={opened ? <IconChevronUp size="1rem" /> : <IconChevronDown size="1rem" />}
          onClick={toggle}
        >
          Ver mais
        </Button>
      </Flex>
      <Text size="xs">Média das notas recebidas pela empresa nos diversos segmentos do questionário</Text>
      {opened && (
        <Flex direction="column">
          <ConditionalWrapper items={resultEmpresa || []}>
            <Divider my="md" />
            <Stack h={400} justify="center" align="center" pt="md" gap="0">
              <Text size="lg" fw="bold">
                Resultado da empresa
              </Text>
              <RadarNota
                datasets={resultEmpresaDataset}
                segments={labels}
                texto="Comparativo da empresa: Nota Oficial e Autoavaliação"
              />
              {/* <Text size="xs">Comparativo da empresa: Autoavaliação e Avaliação do Prêmio</Text> */}
            </Stack>
          </ConditionalWrapper>

          <ConditionalWrapper items={resultRegiao || []}>
            <Divider my="md" />
            <Stack h={400} justify="center" align="center" pt="md" gap="0">
              <Text size="lg" fw="bold">
                Média regional
              </Text>
              <RadarNota
                datasets={resultRegiaoDataset}
                segments={labels}
                texto="Comparativo: Nota Oficial e Média Regional"
              />
            </Stack>
          </ConditionalWrapper>

          <ConditionalWrapper items={resultCategoria || []}>
            <Divider my="md" />
            <Stack h={400} justify="center" align="center" pt="md" gap="0">
              <Text size="lg" fw="bold">
                Média do segmento
              </Text>
              <RadarNota
                datasets={resultCategoriaDataset}
                segments={labels}
                texto="Comparativo: Nota Oficial e Média do Segmento"
              />
            </Stack>
          </ConditionalWrapper>

          <ConditionalWrapper items={resultGeral || []}>
            <Divider my="md" />
            <Stack h={400} justify="center" align="center" pt="md" gap="0">
              <Text size="lg" fw="bold">
                Média geral
              </Text>
              <RadarNota
                datasets={resultGeralDataset}
                segments={labels}
                texto="Comparativo: Nota Oficial e Média Global"
              />
            </Stack>
          </ConditionalWrapper>

          <ConditionalWrapper items={resultPrimeiroLugar || []}>
            <div style={{ display: "none" }}>
              <Divider my="md" />
              <Stack
                h={400}
                justify="center"
                align="center"
                pt="md"
                gap="0"
                style={{
                  visibility: "hidden"
                }}
              >
                <Text size="lg" fw="bold">
                  Primeiro lugar
                </Text>
                <RadarNota
                  datasets={resultPrimeiroLugarDataset}
                  segments={labels}
                  texto="Comparativo: Nota Oficial e Melhor Pontuação"
                />
              </Stack>
            </div>
          </ConditionalWrapper>
          <Divider my="md" />
        </Flex>
      )}
    </Card>
  );
};

export default CardResultados;

/**
 *
 * @param id - id do dataset
 * @param source - dados do dataset
 * @returns - dataset
 */
function buildDataset(
  id: string,
  source: Array<{ segment: string; value?: number; autoAvaliacao?: number }>
): { id: string; data: { segment: string; value: number }[] } {
  return {
    id,
    data: source.map((g) => ({
      segment: g.segment,
      value: g.value ?? g.autoAvaliacao ?? 0
    }))
  };
}
